/**
 * Communications analytics hook — fetches the per-channel analytics payload
 * (summary + daily trend + recent activity) from the Communications API.
 *
 * Uses the SDK's raw HttpClient + getApiUrl (the established fallback pattern in
 * this admin area, e.g. useDialog360). `resp.data` is the unwrapped ResponseData
 * payload (the API wraps results in ResponseData<T>.Ok and the client unwraps it).
 */

import { useState, useCallback } from 'react';
import { useHttpClient } from '@actschurch/ccms-sdk';
import { getApiUrl } from '../../../../config/ccms';

export interface ChannelAnalyticsSummary {
  channel: string;
  total: number;
  sent: number;
  delivered: number;
  read: number;
  failed: number;
  pending: number;
  deliveryRate: number;
  readRate: number;
  windowSize: number;
  capped: boolean;
}

export interface AnalyticsTrendPoint {
  bucket: string;
  total: number;
  sent: number;
  failed: number;
}

export interface RecentActivityItem {
  id: number;
  recipient?: string | null;
  subject?: string | null;
  status?: string | null;
  createdDateTime: number;
}

export interface ChannelAnalytics {
  summary: ChannelAnalyticsSummary;
  trends: AnalyticsTrendPoint[];
  recent: RecentActivityItem[];
}

const path = (p: string) => getApiUrl('communications', `/api/communication-analytics${p}`);

export function useCommunicationAnalytics() {
  const httpClient = useHttpClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ChannelAnalytics | null>(null);

  const fetchChannel = useCallback(
    async (channel: string, recentLimit = 8) => {
      setLoading(true);
      setError(null);
      try {
        const resp = await httpClient.get<ChannelAnalytics>(
          path(`/${channel}?recentLimit=${recentLimit}`),
        );
        setData((resp.data as ChannelAnalytics) ?? null);
      } catch (err: any) {
        setError(err?.message || 'Failed to load analytics');
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [httpClient],
  );

  return { loading, error, data, fetchChannel };
}
